import { CollisionPriority, Data, Draggable, Droppable, UniqueIdentifier } from '@dnd-kit/abstract';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider, useDroppable } from '@dnd-kit/react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { LayoutFieldListItem } from 'routes/essential-workspace/components/right-panel/draggable-list/layout-list-item';
import { CustomFieldPreferences, FieldId, LayoutField } from 'routes/essential-workspace/state/model';
import { Width } from 'state/product-description/types';

import { DraggableListItem } from './draggable-list-item';

export const LIST_ITEM_BASE_CLASS =
  'card rounded-0 border border-secondary border-opacity-40 essential-right-panel-item';

type DraggableListsProps = {
  lockedFields?: LayoutField[];
  visibleFields: LayoutField[];
  additionalFields: LayoutField[];
  onTogglePin: (fieldId: FieldId) => void;
  onUpdateCustomFields: (customFields: CustomFieldPreferences) => void;
  onUpdateFieldWidth: (fieldId: FieldId, width: Width) => void;
};

export type DnDField = LayoutField & {
  id: UniqueIdentifier;
};

const VISIBLE_FIELDS = 'visible-fields';
const ADDITIONAL_FIELDS = 'additional-fields';
type DnDState = Record<typeof VISIBLE_FIELDS | typeof ADDITIONAL_FIELDS, DnDField[]>;

const mapToState = (topFields: LayoutField[], bottomFields: LayoutField[]): DnDState => ({
  [VISIBLE_FIELDS]: topFields.map(f => ({ ...f, id: f.fieldId })),
  [ADDITIONAL_FIELDS]: bottomFields.map(f => ({ ...f, id: f.fieldId })),
});

export type ForbiddenDropOperation = {
  itemId: UniqueIdentifier | null;
  trigger: number;
};
export function DraggableLists({
  lockedFields = [],
  visibleFields,
  additionalFields,
  onUpdateFieldWidth,
  onUpdateCustomFields,
}: DraggableListsProps) {
  const [items, setDnDItems] = useState<DnDState>(mapToState(visibleFields, additionalFields));
  const [forbiddenDrop, setForbiddenDrop] = useState<ForbiddenDropOperation | null>(null);

  useEffect(() => {
    setDnDItems(mapToState(visibleFields, additionalFields));
  }, [visibleFields, additionalFields]);

  const isForbiddenDrop = (source: Draggable<Data> | null, target: Droppable<Data> | null) => {
    const isAlwaysVisible = source?.data.isAlwaysVisible;
    const targetContainer = target?.data.container;

    return isAlwaysVisible && targetContainer === ADDITIONAL_FIELDS;
  };

  return (
    <DragDropProvider
      onDragOver={event => {
        const { source, target } = event.operation;

        if (isForbiddenDrop(source, target)) {
          event.preventDefault();
          return;
        }

        setDnDItems(prev => move(prev, event));
      }}
      onDragEnd={event => {
        const { source, target } = event.operation;

        if (isForbiddenDrop(source, target)) {
          setForbiddenDrop({ itemId: source?.id ?? null, trigger: Date.now() });
          return;
        }
        setForbiddenDrop(null);

        setDnDItems(prev => {
          const newItems = move(prev, event);
          if (!event.operation.canceled) {
            onUpdateCustomFields({
              visibleFields: newItems[VISIBLE_FIELDS].map(({ id: _, ...fields }) => fields),
              additionalConfigurationFields: newItems[ADDITIONAL_FIELDS].map(({ id: _, ...fields }) => fields),
            });
          }
          return newItems;
        });
      }}
    >
      <h2 className="fs-5 fw-normal text-secondary mb-2">
        <FormattedMessage id="essential.workspace.right.panel.main-list" />
      </h2>
      <LockedList items={lockedFields} ariaLabel={'locked-fields'} onUpdateFieldWidth={onUpdateFieldWidth} />
      <DraggableList
        droppableId="visible-fields"
        items={items['visible-fields']}
        draggableGroup={VISIBLE_FIELDS}
        ariaLabel="Visible fields"
        onUpdateFieldWidth={onUpdateFieldWidth}
        forbiddenDrop={forbiddenDrop}
      />

      <div className="essential-right-panel-separator my-3" aria-hidden="true" />

      <div className="essential-right-panel-subsection">
        <h2 className="fs-5 fw-normal text-secondary mb-2">
          <FormattedMessage id="essential.workspace.right.panel.additional-configurations" />
        </h2>
        <DraggableList
          droppableId="additional-fields"
          items={items['additional-fields']}
          draggableGroup={ADDITIONAL_FIELDS}
          ariaLabel="Additional configuration fields"
          onUpdateFieldWidth={onUpdateFieldWidth}
        />
      </div>
    </DragDropProvider>
  );
}

type DraggableListProps = {
  droppableId: string;
  items: DnDField[];
  draggableGroup: string;
  ariaLabel: string;
  forbiddenDrop?: ForbiddenDropOperation | null;
  onUpdateFieldWidth: (fieldId: FieldId, width: Width) => void;
};

type LockedListProps = {
  items: LayoutField[];
  ariaLabel: string;
  onUpdateFieldWidth: (fieldId: FieldId, width: Width) => void;
};

const LockedList = ({ items, ariaLabel, onUpdateFieldWidth }: LockedListProps) => (
  <ul className="list-unstyled d-flex flex-column gap-2 mb-2 p-0" aria-label={ariaLabel}>
    {items.map(field => (
      <li key={field.fieldId} className={LIST_ITEM_BASE_CLASS}>
        <LayoutFieldListItem
          field={field}
          pinState="disabled"
          isDraggable={false}
          disabledPinVariant="outlined"
          onUpdateWidth={onUpdateFieldWidth}
        />
      </li>
    ))}
  </ul>
);

const DraggableList = ({
  droppableId,
  items,
  draggableGroup,
  ariaLabel,
  onUpdateFieldWidth,
  forbiddenDrop,
}: DraggableListProps) => {
  const { ref } = useDroppable({
    id: droppableId,
    type: 'fields-list',
    accept: 'field',
    collisionPriority: CollisionPriority.Low,

    data: {
      container: draggableGroup,
    },
  });

  return (
    <ul
      className="list-unstyled d-flex flex-column gap-2 m-0 p-0"
      aria-label={ariaLabel}
      ref={ref}
      style={{ minHeight: '50px' }}
    >
      {items.map((field, idx) => {
        const showForbiddenTooltip = field.id === forbiddenDrop?.itemId;
        return (
          <DraggableListItem
            key={field.id}
            index={idx}
            draggableGroup={draggableGroup}
            field={field}
            pinState={field.isAlwaysVisible ? 'active' : 'disabled'}
            isDraggable={field.isOrderable}
            onUpdateWidth={onUpdateFieldWidth}
            disabledPinVariant="outlined"
            className={LIST_ITEM_BASE_CLASS}
            showForbiddenTooltip={showForbiddenTooltip}
            forbiddenTooltipTrigger={forbiddenDrop?.trigger}
          />
        );
      })}
    </ul>
  );
};
